export type Flags = {
  commerceWriteEnabled: boolean;
  billingEnabled: boolean;
  personalModeEnabled: boolean;
  bridgeMode: "mock" | "real";
  readOnlyCanaryMode: boolean;
};
export type CommercePlatform = "cafe24" | "naver";
export type Session = {
  platform: CommercePlatform;
  mallId: string;
  displayName: string;
  userId: string;
  shopNo: number;
};
export type Product = {
  productNo: number;
  name: string;
  imageUrl: string | null;
  productUrl: string;
  display: string;
  selling: string;
};
export type ProductDashboardSummary = {
  total: number;
  selling: number;
  hidden: number;
};
export type ProductCategory = {
  categoryNo: number;
  parentCategoryNo: number;
  depth: number;
  name: string;
  fullName: string;
};
export type OutfitCandidate = {
  id: string;
  source: "product_primary" | "product_additional" | "description_image";
  url: string;
  label: string;
  confidence: "high" | "medium" | "low";
  warnings: string[];
};
export type Upload = {
  uploadId: string;
  kind: "outfit" | "person";
  width: number;
  height: number;
  expiresAt: string;
  previewUrl: string;
};
export type CatalogModel = {
  id: string;
  name: string;
  gender: string;
  genderCode?: "female" | "male";
  region?: string;
  detail: string;
  image: string;
  order?: number;
  castingRegion?: string;
  castingMoodLabel?: string;
  castingMoodKey?: string;
  styleMood?: string[];
  recommendedFor?: string[];
  status?: "active" | "candidate" | "rework" | "archived";
};
export type CatalogPreset = {
  code: string;
  title: string;
  subtitle: string;
  image: string;
  framing: string;
  viewAngle: string;
  poseType: string;
  fitFor: string[];
  compatiblePresentations: string[];
  posePresentation: "feminine" | "masculine" | "universal";
  occlusion: string[];
  cameraHeight: string;
  order: number;
  status?: "active" | "candidate" | "disabled" | "archived";
};
export type QualityScores = {
  photorealism: number;
  identity: number;
  garmentFidelity: number;
  anatomy: number;
  sceneAdherence: number;
  editorialImpact: number;
  poseFidelity: number;
  handFootIntegrity: number;
  grounding: number;
  structuralMatch: number;
  backgroundIntegration: number;
  plasticityRisk: number;
  pass: boolean;
  notes: string[];
};
export type Job = {
  id: string;
  batchId: string;
  productNo: number;
  mode: string;
  status: string;
  chargedCredits: number;
  estimatedCredits: number;
  resultUrl: string | null;
  commercePersistenceStatus?: "pending" | "uploading" | "stored" | null;
  /** @deprecated compatibility alias for jobs created before multi-commerce */
  cafe24PersistenceStatus?: "pending" | "uploading" | "stored" | null;
  firebaseBackupExpiresAt?: string | null;
  errorCode: string | null;
  expiresAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  applicationStatus?: string | null;
  progress: number;
  stage: string | null;
  stageLabel: string | null;
  etaSeconds: number | null;
  queuePosition: number | null;
  retryable: boolean;
  simulated: boolean;
  quality: QualityScores | null;
  qualityPassed: boolean;
  outfit?: {
    source?: OutfitCandidate["source"] | "upload";
    candidateId?: string;
    imageUrl?: string;
    uploadId?: string;
    view?: "front" | "back" | "both";
  } | null;
  modelKey?: string | null;
  presetCode?: string | null;
  backgroundTone?: "bright" | "balanced" | "dark";
};

export type CreditSummary = {
  balance: number;
  reserved: number;
  available: number;
  generationCredits: number;
  refundDebt: number;
  items: Array<{
    id: string;
    type: string;
    status: string;
    amount: number;
    delta: number;
    createdAt: string | null;
  }>;
};

export type Cafe24CreditPackage = {
  id: string;
  credits: number;
  amountKrw: number;
  label: string;
};

export type BillingPolicy = {
  mode: "prepaid_credits";
  recurringFeeKrw: number;
  signupCredits: number;
  generationCredits: number;
  refundOnGenerationFailure: boolean;
  purchaseEnabled: boolean;
  purchaseChannel: CommercePlatform;
};

export type RepresentativePreview = {
  productNo: number;
  resultUrl: string;
  currentDigest: string;
  slots: Array<{
    field: "detail_image" | "list_image" | "tiny_image" | "small_image";
    url: string | null;
    width: number | null;
    height: number | null;
  }>;
};

export type DescriptionApplicationOptions = {
  layout: "clean" | "full" | "editorial" | "magazine";
  placement: "top" | "bottom";
  captionTitle?: string;
  captionBody?: string;
};

let csrfToken = "";
export function setCsrfToken(value: string) {
  csrfToken = value;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    cache: "no-store",
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
      ...init?.headers,
    },
  });
  const data =
    response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok)
    throw new Error(data?.error?.code ?? `HTTP_${response.status}`);
  return data as T;
}

export const api = {
  config: () =>
    request<{
      platform: CommercePlatform;
      flags: Flags;
      appBaseUrl: string;
    }>("/api/config"),
  catalog: () =>
    request<{ models: CatalogModel[]; presets: CatalogPreset[] }>(
      "/api/catalog",
    ),
  session: () =>
    request<{ session: Session; csrfToken: string; flags: Flags }>(
      "/api/session",
    ),
  devLogin: (mallId: string) =>
    request<null>("/api/dev/session", {
      method: "POST",
      body: JSON.stringify({ mallId }),
    }),
  products: ({
    offset = 0,
    query = "",
    categoryNo,
    status = "all",
  }: {
    offset?: number;
    query?: string;
    categoryNo?: number | null;
    status?: "all" | "selling" | "hidden";
  } = {}) =>
    request<{ items: Product[]; nextOffset: number | null }>(
      `/api/products?${new URLSearchParams({
        offset: String(offset),
        ...(query.trim() ? { q: query.trim() } : {}),
        ...(categoryNo ? { category_no: String(categoryNo) } : {}),
        ...(status !== "all" ? { status } : {}),
      })}`,
    ),
  productSummary: () =>
    request<ProductDashboardSummary>("/api/products/summary"),
  categories: () =>
    request<{ items: ProductCategory[] }>("/api/categories"),
  outfitCandidates: (productNo: number) =>
    request<{ items: OutfitCandidate[] }>(
      `/api/products/${productNo}/outfit-candidates`,
    ),
  upload: async (kind: "outfit" | "person", file: File) => {
    const response = await fetch(`/api/uploads?kind=${kind}`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": file.type, "X-CSRF-Token": csrfToken },
      body: file,
    });
    const data = await response.json().catch(() => null);
    if (!response.ok)
      throw new Error(data?.error?.code ?? `HTTP_${response.status}`);
    return data as { upload: Upload };
  },
  deleteUpload: (uploadId: string) =>
    request<null>(`/api/uploads/${encodeURIComponent(uploadId)}`, {
      method: "DELETE",
    }),
  jobs: () => request<{ items: Job[] }>("/api/jobs"),
  credits: () => request<{ credits: CreditSummary; billing: BillingPolicy }>("/api/credits"),
  cafe24CreditPackages: () =>
    request<{ packages: Cafe24CreditPackage[] }>(
      "/api/cafe24/billing/packages",
    ),
  createCafe24CreditOrder: (packageId: string) =>
    request<{
      checkoutId: string;
      orderId: string;
      confirmationUrl: string;
    }>("/api/cafe24/billing/orders", {
      method: "POST",
      body: JSON.stringify({ packageId }),
    }),
  reconcileCafe24CreditOrder: (orderId: string) =>
    request<{ payment: { status: "payment_pending" | "paid" | "refunded" } }>(
      `/api/cafe24/billing/orders/${encodeURIComponent(orderId)}`,
    ),
  createJob: (body: object) =>
    request<{ job: Job }>("/api/jobs", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  getJob: (id: string) =>
    request<{ job: Job }>(`/api/jobs/${encodeURIComponent(id)}`),
  applyJob: (id: string, body: DescriptionApplicationOptions) =>
    request<{
      application: {
        status: string;
        imageUrl: string;
        alreadyApplied: boolean;
      };
    }>(`/api/jobs/${encodeURIComponent(id)}/application`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  removeJob: (id: string) =>
    request<{ application: { status: string; alreadyRemoved: boolean } }>(
      `/api/jobs/${encodeURIComponent(id)}/application`,
      { method: "DELETE" },
    ),
  representativePreview: (id: string) =>
    request<{ preview: RepresentativePreview }>(
      `/api/jobs/${encodeURIComponent(id)}/representative-image/preview`,
    ),
  applyRepresentativeImage: (
    id: string,
    body: { mode: "contain" | "cover"; expectedDigest: string },
  ) =>
    request<{
      application: {
        status: string;
        alreadyApplied: boolean;
        productNo: number;
      };
    }>(`/api/jobs/${encodeURIComponent(id)}/representative-image`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  rollbackRepresentativeImage: (id: string) =>
    request<{
      application: { status: string; alreadyRolledBack: boolean };
    }>(`/api/jobs/${encodeURIComponent(id)}/representative-image`, {
      method: "DELETE",
    }),
  logout: () => request<null>("/api/logout", { method: "POST" }),
  disconnect: () => request<null>("/api/disconnect", { method: "POST" }),
};
